import {
  ConnectionState,
  FetchedAssignment,
  RemoteMove,
  RemoteSeatPayload,
  SeatAssignmentError,
  SeatAssignmentErrorKind,
  SeatAssignmentModuleOptions,
  SeatBackend,
  SeatTable,
  SeatUpdate,
} from './types';

/**
 * The Seat Assignment module (ESAPA-104 / mapd-issues#655).
 *
 * It owns the seating model and is the only thing in the application that talks
 * to the backend about seats. Its interface has exactly two kinds of entry
 * point, and this is the load-bearing decision (ADR-0004):
 *
 *  - A **Local Change** (`assign`, `unassign`, `assignMany`, `renameTable`)
 *    mutates the model *and* enqueues a write to the backend.
 *  - A **Remote Change** (`applyRemote`) mutates the model and stops.
 *
 * Publishing is reachable only from the first kind, so an echo loop between two
 * sessions is impossible by construction rather than suppressed by bookkeeping.
 *
 * Outbound writes are coalesced over a short window and flushed as a single
 * batched request carrying only the Tickets that actually changed.
 */
export class SeatAssignmentModule {
  private readonly backend: SeatBackend;
  private readonly coalesceWindowMs: number;
  private readonly onError?: (error: SeatAssignmentError) => void;
  private readonly onChange?: () => void;
  private readonly onRemoteMove?: (move: RemoteMove) => void;

  /** tableId -> Table. */
  private readonly tables = new Map<string, SeatTable>();
  /** Table Label -> set of tableIds carrying it (labels are not unique). */
  private readonly labelIndex = new Map<string, Set<string>>();
  /** ticketId -> tableId. One entry per seated Ticket. */
  private readonly assignments = new Map<string, string>();
  /** ticketId -> SeatNo (Table Label, or "" to unassign) awaiting a flush. */
  private readonly pending = new Map<string, string>();

  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** The request in the air, if any. Never rejects: `flush` handles its own. */
  private inFlight: Promise<void> | null = null;
  private loaded = false;
  private connection: ConnectionState = 'connecting';
  private syncedAt: number | null = null;

  constructor(options: SeatAssignmentModuleOptions) {
    this.backend = options.backend;
    this.coalesceWindowMs = options.coalesceWindowMs ?? 500;
    this.onError = options.onError;
    this.onChange = options.onChange;
    this.onRemoteMove = options.onRemoteMove;
    if (options.tables) this.setTables(options.tables);
  }

  // ---------------------------------------------------------------------------
  // Model queries
  // ---------------------------------------------------------------------------

  /**
   * Replace the room. Assignments are kept; drawing chairs is the view's job.
   * Setting the same room again changes nothing and notifies nobody, so a view
   * may re-publish the room as often as it likes without causing churn.
   */
  setTables(tables: SeatTable[]): void {
    if (this.sameRoom(tables)) return;
    this.tables.clear();
    this.labelIndex.clear();
    for (const table of tables) {
      this.tables.set(table.tableId, { ...table });
      this.indexLabel(table.tableId, table.label);
    }
    this.emitChange();
  }

  getTables(): SeatTable[] {
    return [...this.tables.values()].map((t) => ({ ...t }));
  }

  getTable(tableId: string): SeatTable | undefined {
    const table = this.tables.get(tableId);
    return table ? { ...table } : undefined;
  }

  /** The tableId a Ticket is seated at, or undefined if unseated. */
  getAssignment(ticketId: string): string | undefined {
    return this.assignments.get(ticketId);
  }

  /** Ticket Ids seated at a Table. */
  getTicketsAtTable(tableId: string): string[] {
    const seated: string[] = [];
    this.assignments.forEach((tid, ticketId) => {
      if (tid === tableId) seated.push(ticketId);
    });
    return seated;
  }

  /** How many Tickets are seated at a Table. */
  getOccupancy(tableId: string): number {
    let count = 0;
    this.assignments.forEach((tid) => {
      if (tid === tableId) count += 1;
    });
    return count;
  }

  /** How many Chairs at a Table are still empty. */
  getEmptyChairs(tableId: string): number {
    const table = this.tables.get(tableId);
    if (!table) return 0;
    return Math.max(0, table.chairCount - this.getOccupancy(tableId));
  }

  get connectionState(): ConnectionState {
    return this.connection;
  }

  /**
   * When the model was last rebuilt from the backend, in epoch ms, or null if
   * it never has been. This is what lets the admin see that a recovery
   * actually happened rather than being told the socket came back
   * (mapd-issues#664).
   */
  get lastSyncedAt(): number | null {
    return this.syncedAt;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  hasPendingWrites(): boolean {
    return this.pending.size > 0 || this.inFlight !== null;
  }

  // ---------------------------------------------------------------------------
  // Load / recovery (fetch-based, not poll-based)
  // ---------------------------------------------------------------------------

  /** Fetch Seat Assignments from the backend and rebuild the model. */
  async load(): Promise<void> {
    const fetched = await this.backend.fetchAssignments();
    this.applyFetched(fetched);
    this.loaded = true;
    this.syncedAt = Date.now();
    // The connection state is deliberately left alone. A fetch is HTTP; it says
    // the room is current *now*, not that the realtime socket is up, and
    // claiming otherwise is exactly the silent failure ADR-0004 traded the
    // background poll to avoid. Only the socket marks itself connected.
    this.emitChange();
  }

  /** Refetch on reconnect or on the tab becoming visible. No background poll. */
  async onReconnect(): Promise<void> {
    await this.load();
  }

  setConnectionState(state: ConnectionState): void {
    if (this.connection === state) return;
    this.connection = state;
    this.emitChange();
  }

  private applyFetched(fetched: FetchedAssignment[]): void {
    this.assignments.clear();
    for (const { ticketId, seatNo } of fetched) {
      if (!ticketId || !seatNo) continue;
      const tableId = this.resolveLabel(seatNo);
      if (tableId) this.assignments.set(ticketId, tableId);
      // Unresolved / ambiguous labels are skipped here; the live path
      // (applyRemote) reports them. Fetch is a bulk refresh.
    }
  }

  // ---------------------------------------------------------------------------
  // Local Changes — mutate the model AND enqueue a write
  // ---------------------------------------------------------------------------

  /**
   * Seat a Ticket at a Table. Refused (returns false) if the Table has no empty
   * Chair. Assigning a Ticket already at that Table is a no-op with no write.
   */
  assign(ticketId: string, tableId: string): boolean {
    const table = this.tables.get(tableId);
    if (!table) {
      this.emitError('capacity', `Unknown Table ${tableId}`, [ticketId]);
      return false;
    }
    if (this.assignments.get(ticketId) === tableId) return true;
    if (!this.hasEmptyChair(tableId, ticketId)) {
      this.emitError('capacity', `Table ${table.label} has no empty Chair`, [ticketId]);
      return false;
    }
    this.assignments.set(ticketId, tableId);
    this.enqueue(ticketId, table.label);
    this.emitChange();
    return true;
  }

  /** Unseat a Ticket. A Ticket that is not seated issues no write. */
  unassign(ticketId: string): void {
    if (!this.assignments.has(ticketId)) return;
    this.assignments.delete(ticketId);
    this.enqueue(ticketId, '');
    this.emitChange();
  }

  /**
   * Seat several Tickets at one Table in a single action. Tickets that do not
   * fit are refused. All that fit are coalesced into a single request.
   */
  assignMany(ticketIds: string[], tableId: string): { assigned: string[]; refused: string[] } {
    const table = this.tables.get(tableId);
    if (!table) {
      this.emitError('capacity', `Unknown Table ${tableId}`, ticketIds);
      return { assigned: [], refused: [...ticketIds] };
    }
    const assigned: string[] = [];
    const refused: string[] = [];
    for (const ticketId of ticketIds) {
      if (this.assignments.get(ticketId) === tableId) {
        assigned.push(ticketId);
        continue;
      }
      if (!this.hasEmptyChair(tableId, ticketId)) {
        refused.push(ticketId);
        continue;
      }
      this.assignments.set(ticketId, tableId);
      this.enqueue(ticketId, table.label);
      assigned.push(ticketId);
    }
    if (refused.length) {
      this.emitError('capacity', `Table ${table.label} could not seat ${refused.length} Ticket(s)`, refused);
    }
    this.emitChange();
    return { assigned, refused };
  }

  /**
   * Rename a Table. Because the backend stores the Table Label (ADR-0002), this
   * rewrites `SeatNo` for every attendee seated at the Table. It changes no Seat
   * Assignment. A Table with nobody seated at it issues no write.
   */
  renameTable(tableId: string, label: string): void {
    const table = this.tables.get(tableId);
    if (!table || table.label === label) return;
    this.deindexLabel(tableId, table.label);
    table.label = label;
    this.indexLabel(tableId, label);
    for (const ticketId of this.getTicketsAtTable(tableId)) {
      this.enqueue(ticketId, label);
    }
    this.emitChange();
  }

  // ---------------------------------------------------------------------------
  // Remote Change — mutate the model and STOP. Never enqueues a write.
  // ---------------------------------------------------------------------------

  /**
   * Apply an inbound realtime payload. Resolving the inbound `SeatNo` back to a
   * Table by its Label can be ambiguous or miss (ADR-0002); both are handled
   * explicitly and reported, never crashed on. This path never writes.
   */
  applyRemote(raw: RemoteSeatPayload): void {
    const parsed = parseRemotePayload(raw);
    if (!parsed || !parsed.ticketId) return;
    const { ticketId, seatNo } = parsed;

    // The seat-bearing channels also carry payloads that say nothing about
    // seating — a check-in, a profile edit. A payload with no seat field is
    // not an unseat; reading it as one would empty a Table on every check-in.
    if (seatNo === undefined) return;

    if (!seatNo) {
      this.applyRemoteUnseat(ticketId);
      return;
    }

    const matches = this.labelIndex.get(seatNo);
    if (!matches || matches.size === 0) {
      this.emitError('unresolved-remote', `Inbound seat "${seatNo}" matches no Table`, [ticketId]);
      return;
    }
    if (matches.size > 1) {
      this.emitError('ambiguous-remote', `Inbound seat "${seatNo}" matches ${matches.size} Tables`, [ticketId]);
      return;
    }
    const tableId = matches.values().next().value as string;
    const previousTableId = this.assignments.get(ticketId);
    if (previousTableId !== tableId) {
      this.assignments.set(ticketId, tableId);
      // A remote move of an attendee already seated elsewhere is surfaced, so
      // the admin sees why the canvas changed rather than it swapping silently.
      if (previousTableId) {
        this.onRemoteMove?.({ ticketId, fromTableId: previousTableId, toTableId: tableId });
      }
      this.emitChange();
    }
  }

  /**
   * A Remote Change that says a Ticket is not seated anywhere — its booking was
   * cancelled elsewhere, so there is nobody left to seat. Like every Remote
   * Change this mutates the model and stops: it never writes (ADR-0004).
   */
  applyRemoteUnseat(ticketId: string): void {
    if (!ticketId || !this.assignments.has(ticketId)) return;
    this.assignments.delete(ticketId);
    this.emitChange();
  }

  // ---------------------------------------------------------------------------
  // Write coalescing
  // ---------------------------------------------------------------------------

  /**
   * Flush any pending writes immediately. Called on page-hide and before
   * navigation so the most recent Seat Assignment is not lost in the window.
   *
   * A Local Change made while a request is already in the air is not in that
   * request, so waiting for the request to settle and then flushing again is
   * what makes this drain rather than return early (US1, US21).
   */
  async flushPending(): Promise<void> {
    if (this.inFlight) await this.inFlight;
    await this.flush();
  }

  private enqueue(ticketId: string, seatNo: string): void {
    this.pending.set(ticketId, seatNo);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, this.coalesceWindowMs);
  }

  private flush(): Promise<void> {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    // A second request would race the first one's writes; what accumulates
    // meanwhile is sent by the reschedule at the end of `sendPending`.
    if (this.inFlight) return this.inFlight;
    if (this.pending.size === 0) return Promise.resolve();

    this.inFlight = this.sendPending();
    return this.inFlight;
  }

  private async sendPending(): Promise<void> {
    const batch = new Map(this.pending);
    this.pending.clear();
    const updates: SeatUpdate[] = [...batch].map(([Id, SeatNo]) => ({ Id, SeatNo }));

    try {
      const results = await this.backend.updateSeats(updates);
      const failed = updates.filter((u) => {
        const result = results.find((r) => r.Id === u.Id);
        return !result || !result.Success;
      });
      if (failed.length) {
        this.requeue(failed);
        const kind: SeatAssignmentErrorKind =
          failed.length === updates.length ? 'write-failed' : 'write-partial';
        this.emitError(kind, `${failed.length} seat write(s) failed; retrying`, failed.map((u) => u.Id));
      }
    } catch (error) {
      this.requeue(updates);
      this.emitError('write-failed', `Seat write failed; retrying: ${describeError(error)}`, updates.map((u) => u.Id));
    } finally {
      this.inFlight = null;
      // Anything requeued after a failure, and anything the admin changed while
      // the request was in the air, goes out in the next window.
      if (this.pending.size > 0) this.scheduleFlush();
    }
  }

  /** Re-enqueue failed writes for retry, unless superseded by a newer change. */
  private requeue(updates: SeatUpdate[]): void {
    for (const update of updates) {
      if (!this.pending.has(update.Id)) this.pending.set(update.Id, update.SeatNo);
    }
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  /** Whether the given room is the one already loaded, Table for Table. */
  private sameRoom(tables: SeatTable[]): boolean {
    if (tables.length !== this.tables.size) return false;
    return tables.every((table) => {
      const current = this.tables.get(table.tableId);
      return (
        !!current && current.label === table.label && current.chairCount === table.chairCount
      );
    });
  }

  private hasEmptyChair(tableId: string, ignoringTicketId?: string): boolean {
    const table = this.tables.get(tableId);
    if (!table) return false;
    let occupancy = this.getOccupancy(tableId);
    if (ignoringTicketId && this.assignments.get(ignoringTicketId) === tableId) {
      occupancy -= 1;
    }
    return occupancy < table.chairCount;
  }

  private resolveLabel(label: string): string | undefined {
    const matches = this.labelIndex.get(label);
    if (!matches || matches.size !== 1) return undefined;
    return matches.values().next().value as string;
  }

  private indexLabel(tableId: string, label: string): void {
    let set = this.labelIndex.get(label);
    if (!set) {
      set = new Set<string>();
      this.labelIndex.set(label, set);
    }
    set.add(tableId);
  }

  private deindexLabel(tableId: string, label: string): void {
    const set = this.labelIndex.get(label);
    if (!set) return;
    set.delete(tableId);
    if (set.size === 0) this.labelIndex.delete(label);
  }

  private emitError(kind: SeatAssignmentErrorKind, message: string, ticketIds?: string[]): void {
    this.onError?.({ kind, message, ticketIds });
  }

  private emitChange(): void {
    this.onChange?.();
  }
}

/** The keys a seat value has been seen under on the realtime channels. */
const SEAT_KEYS = ['SeatNo__c', 'Seat_No__c', 'Seat__c', 'Tag_No__c', 'seatNo', 'SeatNo'];

/**
 * Extract the Ticket Id and seat value from a raw realtime payload. The seat
 * field name is not settled with the Apex owner (see the parent PRD), so a few
 * candidate keys are accepted defensively — `Tag_No__c` among them, because
 * that is the one the `attendeeProfile` channel actually sends.
 *
 * A seat the payload does not mention and a seat the payload clears are
 * different facts, so they are reported differently: `undefined` means the
 * payload carries no seating information at all, `''` means the seat was
 * cleared. Collapsing the two would turn every check-in into an unseat.
 */
export function parseRemotePayload(
  raw: RemoteSeatPayload,
): { ticketId: string; seatNo: string | undefined } | null {
  if (!raw) return null;
  const source = (raw.payload ?? raw) as Record<string, unknown>;
  if (!source || typeof source !== 'object') return null;

  const ticketId = firstString(source, ['Ticket_Id__c', 'ticketId', 'TicketId', 'Id']);
  if (!ticketId) return null;

  const seatNo = seatValue(source, SEAT_KEYS);
  return { ticketId, seatNo: seatNo === undefined ? undefined : seatNo.trim() };
}

/**
 * The seat value a payload carries: the first candidate key that is present,
 * read as a cleared seat when it holds null and stringified when it holds
 * anything else. `undefined` only when no candidate key appears at all.
 */
function seatValue(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    if (!(key in source)) continue;
    const value = source[key];
    if (typeof value === 'string') return value;
    if (value === null || value === undefined) return '';
    return `${value}`;
  }
  return undefined;
}

function firstString(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
